// FORGE-166: assemble a concrete McpServer from a catalog entry's friendly
// fields. Pure + framework-free so it's unit-tested independently of the modal.
//
// The modal collects { fieldKey: value } plus (for AWS-backed servers) the
// cross-account role inputs. This maps them onto the underlying McpServer,
// keeping the raw command/args/transport out of the user's hands.

import type { McpServer } from '../types/projects';
import type { McpCatalogEntry } from '../types/mcpCatalog';

/** A pasted value that looks like a Secrets Manager ARN → store as auth_secret_arn. */
export const SECRET_ARN_RE = /^arn:aws:secretsmanager:/i;

export interface AwsAccountInput {
  roleArn?: string;
  region?: string;
  externalId?: string;
}

/**
 * Build the McpServer for a catalog entry from the user's field values.
 * `values` is keyed by McpConfigField.key. `aws` carries the cross-account
 * inputs (only relevant for AWS-backed servers).
 */
export function assembleServerFromCatalog(
  entry: McpCatalogEntry,
  name: string,
  values: Record<string, string>,
  aws?: AwsAccountInput,
): McpServer {
  const t = entry.template;
  const server: McpServer = {
    name: name.trim(),
    transport: t.transport,
    ...(t.command ? { command: t.command } : {}),
    args: t.args ? [...t.args] : [],
    ...(t.url ? { url: t.url } : {}),
  };

  const env: Record<string, string> = { ...(t.defaultEnv ?? {}) };

  for (const f of t.fields ?? []) {
    const raw = (values[f.key] ?? '').trim();
    if (!raw) continue;
    switch (f.bind.into) {
      case 'env':
        env[f.bind.envKey] = raw;
        break;
      case 'authToken':
        if (SECRET_ARN_RE.test(raw)) server.auth_secret_arn = raw;
        else server.auth_token = raw;
        break;
      case 'awsRole':
        server.aws_assume_role_arn = raw;
        break;
      case 'awsRegion':
        server.aws_region = raw;
        break;
      case 'kubeconfig':
        // FORGE-169: file content the agent writes to a temp file + points
        // KUBECONFIG at. Never goes in env/argv.
        server.kubeconfig = raw;
        break;
    }
  }

  // Cross-account AWS section (role/region/external id) — collected outside the
  // declared fields for AWS-backed servers.
  if (aws?.roleArn?.trim()) {
    server.aws_assume_role_arn = aws.roleArn.trim();
    if (aws.region?.trim()) server.aws_region = aws.region.trim();
    if (aws.externalId?.trim()) server.aws_external_id = aws.externalId.trim();
  }

  if (Object.keys(env).length > 0) server.env = env;
  return server;
}

/**
 * Keys of required fields that are still empty — used to block save and show
 * which inputs are missing.
 */
export function missingRequiredFields(
  entry: McpCatalogEntry,
  values: Record<string, string>,
): string[] {
  return (entry.template.fields ?? [])
    .filter((f) => f.required && !(values[f.key] ?? '').trim())
    .map((f) => f.key);
}
